/**
 * VireoHR - Owner Business Settings
 * PRD v4.1: Penalty Configuration (Money or Hours)
 */

import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { firestore } from '@/lib/firebase/client';
import { functions } from '@/services/cloudFunctions';


interface PenaltySettings {
  // Penalties master toggle
  penaltiesEnabled: boolean;
  
  // Late penalty configuration
  latePenaltyType: 'money' | 'hours';
  latePenaltyAmount: number; // JOD or hours
  
  // No-show penalty configuration
  noShowPenaltyType: 'money' | 'hours';
  noShowPenaltyAmount: number; // JOD or hours
  
  // Overtime toggle
  overtimeEnabled: boolean;
}

// PRD v4.1: Validation constants
const MAX_MONEY_PENALTY = 100; // JOD
const MAX_HOURS_PENALTY = 24; // hours per occurrence

const DEFAULT_SETTINGS: PenaltySettings = {
  penaltiesEnabled: true,
  latePenaltyType: 'money',
  latePenaltyAmount: 5,
  noShowPenaltyType: 'money',
  noShowPenaltyAmount: 15,
  overtimeEnabled: false,
};

export default function BusinessSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState<PenaltySettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.tenantId) {
      loadSettings();
    }
  }, [user?.tenantId]);

  const loadSettings = async () => {
    if (!user?.tenantId) return;

    try {
      setLoading(true);
      const settingsRef = doc(firestore, 'settings', user.tenantId);
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setSettings({
          penaltiesEnabled: data.penaltiesEnabled !== undefined ? data.penaltiesEnabled : DEFAULT_SETTINGS.penaltiesEnabled,
          latePenaltyType: data.latePenaltyType || DEFAULT_SETTINGS.latePenaltyType,
          latePenaltyAmount: data.latePenaltyAmount || DEFAULT_SETTINGS.latePenaltyAmount,
          noShowPenaltyType: data.noShowPenaltyType || DEFAULT_SETTINGS.noShowPenaltyType,
          noShowPenaltyAmount: data.noShowPenaltyAmount || DEFAULT_SETTINGS.noShowPenaltyAmount,
          overtimeEnabled: data.overtimeEnabled !== undefined ? data.overtimeEnabled : DEFAULT_SETTINGS.overtimeEnabled,
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user?.tenantId) return;

    // Frontend validation (first line of defense)
    // Note: Backend will also validate (secure layer)
    
    // Check for positive numbers
    if (settings.latePenaltyAmount < 0 || settings.noShowPenaltyAmount < 0) {
      toast.error('Penalty amounts must be positive');
      return;
    }

    // Check for valid numbers (not NaN or Infinity)
    if (isNaN(settings.latePenaltyAmount) || !isFinite(settings.latePenaltyAmount)) {
      toast.error('Late penalty must be a valid number');
      return;
    }
    
    if (isNaN(settings.noShowPenaltyAmount) || !isFinite(settings.noShowPenaltyAmount)) {
      toast.error('No-show penalty must be a valid number');
      return;
    }

    // Check maximum caps
    if (settings.latePenaltyType === 'money' && settings.latePenaltyAmount > MAX_MONEY_PENALTY) {
      toast.error(`Late penalty cannot exceed ${MAX_MONEY_PENALTY} JOD`);
      return;
    }

    if (settings.latePenaltyType === 'hours' && settings.latePenaltyAmount > MAX_HOURS_PENALTY) {
      toast.error(`Late penalty cannot exceed ${MAX_HOURS_PENALTY} hours per occurrence`);
      return;
    }

    if (settings.noShowPenaltyType === 'money' && settings.noShowPenaltyAmount > MAX_MONEY_PENALTY) {
      toast.error(`No-show penalty cannot exceed ${MAX_MONEY_PENALTY} JOD`);
      return;
    }

    if (settings.noShowPenaltyType === 'hours' && settings.noShowPenaltyAmount > MAX_HOURS_PENALTY) {
      toast.error(`No-show penalty cannot exceed ${MAX_HOURS_PENALTY} hours per occurrence`);
      return;
    }

    try {
      setSaving(true);
      
      // CRITICAL P0 FIX: Call secure Cloud Function with backend validation
      // This prevents malicious owners from bypassing frontend validation
      const updateSettings = httpsCallable(functions, 'updateBusinessSettings');
      
      const result = await updateSettings({
        tenantId: user.tenantId,
        penaltiesEnabled: settings.penaltiesEnabled,
        latePenaltyType: settings.latePenaltyType,
        latePenaltyAmount: settings.latePenaltyAmount,
        noShowPenaltyType: settings.noShowPenaltyType,
        noShowPenaltyAmount: settings.noShowPenaltyAmount,
        overtimeEnabled: settings.overtimeEnabled
      });

      // Refresh settings from server to ensure consistency
      await loadSettings();
      
      toast.success('Settings saved successfully');
      
    } catch (error: any) {
      console.error('Error saving settings:', error);
      
      // Show backend validation errors to user
      const errorMessage = error.message || 'Failed to save settings';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Business Settings">
        <div className="flex justify-center items-center h-64">
          <div className="text-[#0D9488]">Loading settings...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Business Settings">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#0D9488] mb-2">Business Settings</h1>
          <p className="text-[#D4AF37]">Configure penalties and overtime policies</p>
        </div>

        {/* Penalties Master Toggle */}
        <Card className="mb-6">
          <h2 className="text-xl font-semibold text-[#0D9488] mb-4">Penalty System</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-[#F8F9FA] rounded-lg">
              <div>
                <h3 className="font-semibold text-[#0D9488] mb-1">Enable Penalties</h3>
                <p className="text-sm text-[#D4AF37]">
                  When enabled, late arrivals and no-shows will be penalized according to configured rules.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.penaltiesEnabled}
                  onChange={(e) => setSettings({ ...settings, penaltiesEnabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#0D9488]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0D9488]"></div>
              </label>
            </div>
          </div>
        </Card>

        {/* Overtime Settings */}
        <Card className="mb-6">
          <h2 className="text-xl font-semibold text-[#0D9488] mb-4">Overtime Policy</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-[#F8F9FA] rounded-lg">
              <div>
                <h3 className="font-semibold text-[#0D9488] mb-1">Enable Overtime</h3>
                <p className="text-sm text-[#D4AF37]">
                  When enabled, employees can work beyond scheduled hours. 
                  When disabled, system auto-clocks out at shift end.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.overtimeEnabled}
                  onChange={(e) => setSettings({ ...settings, overtimeEnabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#0D9488]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0D9488]"></div>
              </label>
            </div>
          </div>
        </Card>

        {/* Late Penalty Configuration */}
        <Card className="mb-6">
          <h2 className="text-xl font-semibold text-[#0D9488] mb-4">Late Arrival Penalty</h2>
          <div className="space-y-4">
            {/* Penalty Type */}
            <div>
              <label className="block text-sm font-medium text-[#D4AF37] mb-2">
                Penalty Type
              </label>
              <div className="flex gap-4">
                <button
                  onClick={() => setSettings({ ...settings, latePenaltyType: 'money' })}
                  disabled={saving}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                    settings.latePenaltyType === 'money'
                      ? 'border-[#0D9488] bg-[#0D9488]/10 text-[#0D9488] font-semibold'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  💰 Money (JOD)
                </button>
                <button
                  onClick={() => setSettings({ ...settings, latePenaltyType: 'hours' })}
                  disabled={saving}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                    settings.latePenaltyType === 'hours'
                      ? 'border-[#0D9488] bg-[#0D9488]/10 text-[#0D9488] font-semibold'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  ⏰ Hours Deduction
                </button>
              </div>
            </div>

            {/* Penalty Amount */}
            <div>
              <label className="block text-sm font-medium text-[#D4AF37] mb-2">
                Amount per Late Arrival
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max={settings.latePenaltyType === 'money' ? MAX_MONEY_PENALTY : MAX_HOURS_PENALTY}
                  step="0.1"
                  value={settings.latePenaltyAmount}
                  onChange={(e) => setSettings({ ...settings, latePenaltyAmount: parseFloat(e.target.value) || 0 })}
                  disabled={saving}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#0D9488] focus:outline-none text-[#0D9488] font-semibold disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#D4AF37] font-medium">
                  {settings.latePenaltyType === 'money' ? 'JOD' : 'hours'}
                </span>
              </div>
              <p className="text-xs text-[#D4AF37] mt-1">
                {settings.latePenaltyType === 'money' 
                  ? `Amount deducted from payroll per late occurrence (Max: ${MAX_MONEY_PENALTY} JOD)`
                  : `Hours deducted from total worked hours per late occurrence (Max: ${MAX_HOURS_PENALTY} hours)`
                }
              </p>
            </div>
          </div>
        </Card>

        {/* No-Show Penalty Configuration */}
        <Card className="mb-6">
          <h2 className="text-xl font-semibold text-[#0D9488] mb-4">No-Show Penalty</h2>
          <div className="space-y-4">
            {/* Penalty Type */}
            <div>
              <label className="block text-sm font-medium text-[#D4AF37] mb-2">
                Penalty Type
              </label>
              <div className="flex gap-4">
                <button
                  onClick={() => setSettings({ ...settings, noShowPenaltyType: 'money' })}
                  disabled={saving}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                    settings.noShowPenaltyType === 'money'
                      ? 'border-[#0D9488] bg-[#0D9488]/10 text-[#0D9488] font-semibold'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  💰 Money (JOD)
                </button>
                <button
                  onClick={() => setSettings({ ...settings, noShowPenaltyType: 'hours' })}
                  disabled={saving}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                    settings.noShowPenaltyType === 'hours'
                      ? 'border-[#0D9488] bg-[#0D9488]/10 text-[#0D9488] font-semibold'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  ⏰ Hours Deduction
                </button>
              </div>
            </div>

            {/* Penalty Amount */}
            <div>
              <label className="block text-sm font-medium text-[#D4AF37] mb-2">
                Amount per No-Show
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max={settings.noShowPenaltyType === 'money' ? MAX_MONEY_PENALTY : MAX_HOURS_PENALTY}
                  step="0.1"
                  value={settings.noShowPenaltyAmount}
                  onChange={(e) => setSettings({ ...settings, noShowPenaltyAmount: parseFloat(e.target.value) || 0 })}
                  disabled={saving}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#0D9488] focus:outline-none text-[#0D9488] font-semibold disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#D4AF37] font-medium">
                  {settings.noShowPenaltyType === 'money' ? 'JOD' : 'hours'}
                </span>
              </div>
              <p className="text-xs text-[#D4AF37] mt-1">
                {settings.noShowPenaltyType === 'money' 
                  ? `Amount deducted from payroll per no-show occurrence (Max: ${MAX_MONEY_PENALTY} JOD)`
                  : `Hours deducted from total worked hours per no-show occurrence (Max: ${MAX_HOURS_PENALTY} hours)`
                }
              </p>
            </div>
          </div>
        </Card>

        {/* Summary Card */}
        <Card className="mb-6 bg-[#F8F9FA]">
          <h3 className="text-lg font-semibold text-[#0D9488] mb-3">Current Configuration</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[#D4AF37]">Penalties:</span>
              <Badge variant={settings.penaltiesEnabled ? 'success' : 'warning'}>
                {settings.penaltiesEnabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-[#D4AF37]">Overtime:</span>
              <Badge variant={settings.overtimeEnabled ? 'success' : 'warning'}>
                {settings.overtimeEnabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-[#D4AF37]">Late Penalty:</span>
              <span className="text-[#0D9488] font-semibold">
                {settings.latePenaltyAmount} {settings.latePenaltyType === 'money' ? 'JOD' : 'hours'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#D4AF37]">No-Show Penalty:</span>
              <span className="text-[#0D9488] font-semibold">
                {settings.noShowPenaltyAmount} {settings.noShowPenaltyType === 'money' ? 'JOD' : 'hours'}
              </span>
            </div>
          </div>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end gap-4">
          <Button
            variant="secondary"
            onClick={loadSettings}
            disabled={saving}
          >
            Reset
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </Layout>
  );
}
